import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBriefcase,
  FaBuilding,
  FaCheck,
  FaChevronLeft,
  FaCopy,
  FaEnvelope,
  FaExclamation,
  FaFileAlt,
  FaGlobe,
  FaIndustry,
  FaLayerGroup,
  FaPhone,
  FaSync,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import BrokerAdminLabel from "../../components/Broker/BrokerAdminLabel";
import BrokerAlert from "../../components/Broker/BrokerAlert";
import BrokerNotesPanel from "../../components/Broker/BrokerNotesPanel";
import BrokerSkeleton from "../../components/Broker/BrokerSkeleton";
import KycStatusBadge from "../../components/Broker/KycStatusBadge";
import StatusBadge from "../../components/Broker/StatusBadge";
import TagPicker from "../../components/TagPicker";
import { resolveKycSubjectUrl } from "../../components/Broker/kycLink";
import { buildBrokerPath, useBrokerBasePath } from "../../components/Broker/brokerPaths";
import { DialogContext } from "../../store/context/DialogContext";
import { BROKER_APPLICATION_NOTE_RECORD_TYPE, BrokerService } from "./BrokerService";
import { getBrokerProductByFormType } from "./brokerProductRegistry";
import { brokerTagStore } from "./brokerTagStore";
import { FORM_TYPE, getFormTypeLabel } from "../../util/applicationLabels";
import { isOwnerKycRequired } from "../../util/kycRules";
import { getBrokerVisibleSystemTags } from "./brokerSystemTags";

const BUSINESS_HEADER_FACTS = [
  { key: "Business_Name", label: "Legal name", icon: FaBuilding },
  { key: "Doing_Business_As", label: "DBA", icon: FaBriefcase },
  { key: "Entity_Type", label: "Entity", icon: FaLayerGroup },
  { key: "Business_Registration_Country", label: "Country", icon: FaGlobe },
  { key: "Business_Industry_Type", label: "Industry", icon: FaIndustry },
];

// Individual Plus detail facts (the record is the IndividualLite; name is combined separately).
const INDIVIDUAL_HEADER_FACTS = [
  { key: "email", label: "Email", icon: FaEnvelope },
  { key: "mobilePhone", label: "Phone", icon: FaPhone },
  { key: "nationality", label: "Nationality", icon: FaGlobe },
];

// "DD MMM YYYY HH:MM" — e.g. "10 Jun 2026 17:51".
const formatDateTimeShort = (value) => {
  if (!value) return "—";
  const timestamp = Number(value);
  const date = Number.isFinite(timestamp) ? new Date(timestamp * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const datePart = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} ${timePart}`;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value;
};

const getStatusKey = (status) => String(status || "").toUpperCase();
const isSubmitted = (status) => status === 1 || getStatusKey(status) === "SUBMITTED";


const initials = (value) => {
  const parts = String(value || "Draft client").split(/[^A-Za-z0-9]+/).filter(Boolean);
  return (parts.length ? parts.slice(0, 2).map((part) => part[0]).join("") : "DC").toUpperCase();
};

const fullName = (person = {}) => (
  [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ").trim()
);

const ownerTypeLabel = (person = {}) => {
  if (person.isPrimaryUser) return "Primary user";
  if (person.isAuthorizedUser) return "Authorized user";
  if (person.isControlPerson) return "Control person";
  if (person.isShareHolder) return "Beneficial owner";
  return "Individual";
};

// Map the various owner status shapes to the KycStatusBadge vocabulary.
const toKycStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "sent") return "SENT";
  if (normalized === "opened") return "OPENED";
  if (normalized === "in_progress") return "IN_PROGRESS";
  if (normalized === "expired") return "EXPIRED";
  if (normalized === "completed" || normalized === "kyc_complete") return "COMPLETED";
  if (normalized === "failed" || normalized === "rejected") return "FAILED";
  if (normalized === "active" || normalized === "generated") return "GENERATED";
  return "NOT_GENERATED";
};

// Ondato KYC flowStatus -> KycStatusBadge vocabulary.
//   1/3/5 = done, 4 = rejected (failed), 2 = an FV Bank reviewer pressed "Redo
//   KYC" so the owner must resubmit, 0 = pending. flowStatus 2 is reached only
//   via redo, so surface it distinctly (the owner was emailed a fresh link).
//   Note: the record is created with flowStatus 0 the moment the KYC link is
//   generated/emailed (createdBy "System"), BEFORE the owner does anything, and
//   Ondato never reports a true mid-verification state here — so 0 means "Not
//   started", not "In progress". No record at all -> null (also "Not started").
const ondatoToKyc = (flowStatus) => {
  if (flowStatus === null || flowStatus === undefined) return null;
  // Matches BusinessPlusKYCStatus: 1/3/5 = Completed, 4 = Rejected, 0/2 = Pending.
  if (flowStatus === 1 || flowStatus === 3 || flowStatus === 5) return "COMPLETED";
  if (flowStatus === 4) return "FAILED";
  if (flowStatus === 2) return "REDO_REQUESTED";
  return "NOT_GENERATED";
};

const DetailSkeleton = () => (
  <div className="broker-page broker-application-detail-page">
    <div className="broker-detail-card">
      <BrokerSkeleton rows={10} variant="detail" />
    </div>
  </div>
);

const BrokerApplicationDetail = ({ embedded = false, onClose } = {}) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const brokerBasePath = useBrokerBasePath();
  const { showError, showMessage } = useContext(DialogContext) || {};
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [brokerTags, setBrokerTags] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const editPath = buildBrokerPath(brokerBasePath, `/applications/${applicationId}/edit`);

  const openEditInNewTab = () => {
    window.open(editPath, "_blank", "noopener,noreferrer");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch the application form first to learn its formType, then resolve the product-specific
      // detail + fields-json endpoints from the registry (business → businessDetail/Business group;
      // individual → individualDetail/Individuals_Lite group). Scalable: a new product = a registry entry.
      const detailResponse = await BrokerService.detail(applicationId);
      const application = detailResponse?.data || {};
      const product = getBrokerProductByFormType(application.formType);
      const detailMethod = product?.service?.detail || "businessDetail";
      const fieldsMethod = product?.service?.fields || "businessFields";
      const [recordResponse, kycResponse, fieldsResponse] = await Promise.all([
        BrokerService[detailMethod](applicationId).catch(() => null),
        BrokerService.kycStatuses(applicationId).catch(() => null),
        BrokerService[fieldsMethod]().catch(() => null),
      ]);
      setPayload({
        application,
        business: recordResponse?.data || {},
        kyc: kycResponse?.data?.rows || [],
        fields: Array.isArray(fieldsResponse?.data?.fields) ? fieldsResponse.data.fields : [],
      });
    } catch (loadError) {
      setError("Unable to load application.");
      showError?.(loadError || "Unable to load application.");
    } finally {
      setLoading(false);
    }
  }, [applicationId, showError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    brokerTagStore.fetch().catch(() => {});
  }, []);

  const tagVersionRef = useRef(0);

  // Keep local broker-tags in sync with the loaded application, remember the
  // version for optimistic-lock-safe writes, and feed the suggestions store.
  useEffect(() => {
    const app = payload?.application;
    const tags = app?.brokerTags;
    setBrokerTags(Array.isArray(tags) ? tags : []);
    if (app) tagVersionRef.current = Number(app.version) || 0;
    brokerTagStore.record(tags);
  }, [payload]);

  const saveBrokerTags = useCallback(async (nextTags) => {
    setSavingTags(true);
    try {
      const registeredTags = await brokerTagStore.ensureRegisteredTags(nextTags);
      setBrokerTags(registeredTags);
      brokerTagStore.record(registeredTags);
      const res = await BrokerService.updateBrokerTags(applicationId, registeredTags, tagVersionRef.current);
      const newVersion = res?.data?.version;
      if (newVersion !== undefined && newVersion !== null) tagVersionRef.current = Number(newVersion);
    } catch (tagError) {
      // Most likely an optimistic-lock (version) conflict — refetch the latest
      // version and retry the same change once before surfacing the error.
      try {
        const latest = await BrokerService.detail(applicationId);
        const freshVersion = latest?.data?.version;
        if (freshVersion !== undefined && freshVersion !== null) tagVersionRef.current = Number(freshVersion);
        const registeredTags = await brokerTagStore.ensureRegisteredTags(nextTags);
        setBrokerTags(registeredTags);
        brokerTagStore.record(registeredTags);
        const retry = await BrokerService.updateBrokerTags(applicationId, registeredTags, tagVersionRef.current);
        const retryVersion = retry?.data?.version;
        if (retryVersion !== undefined && retryVersion !== null) tagVersionRef.current = Number(retryVersion);
      } catch (retryError) {
        showError?.(retryError || tagError || "Unable to update tags.");
        const reverted = payload?.application?.brokerTags;
        setBrokerTags(Array.isArray(reverted) ? reverted : []);
      }
    } finally {
      setSavingTags(false);
    }
  }, [applicationId, showError, payload]);

  const application = payload?.application || {};
  const business = payload?.business || {};
  // Individual Plus has a single subject (the applicant) rather than a business UBO list.
  const isIndividualPlus = application.formType === "INDIVIDUAL_PLUS";
  const submitted = isSubmitted(application.status);
  // FV Bank's "Missing Info" / "KYC Failed" system tags, surfaced read-only.
  const systemTags = getBrokerVisibleSystemTags(application.tags);
  // Per-step statuses from the real application steps. The overall status flips
  // to 2 for KYC redos too, so the "update the application form" prompt must key
  // off the actual form steps — not the overall status — to avoid asking the
  // broker to edit a form that FV Bank already approved.
  const stepStatusByLabel = (Array.isArray(application.steps) ? application.steps : [])
    .reduce((acc, step) => { if (step?.label) acc[step.label] = Number(step.status); return acc; }, {});
  const hasSteps = Object.keys(stepStatusByLabel).length > 0;
  const formStepApproved = hasSteps && stepStatusByLabel["Application Form"] === 3;
  // FV asked for more info on the form (Application Form / Document Verification
  // in a 2/7 state). Falls back to the overall status when step data is absent.
  const needsInfo = hasSteps
    ? ([2, 7].includes(stepStatusByLabel["Application Form"]) || [2, 7].includes(stepStatusByLabel["Document Verification"]))
    : [2, 7].includes(Number(application.status));
  // Account creation failed (APPLICATION_STATUS 7) is a downstream FV-side provisioning
  // failure AFTER the form was approved and KYC completed — not something the broker fixes
  // by editing the form. Treat it as "still with FV Bank / verification in progress", never
  // as an Application Form error or a missing-info request.
  const accountCreationFailed = Number(application.status) === 7;
  // The "Application Form" tracker stage covers form + document submission, so it's
  // complete once the Application Form step is submitted/approved AND (when a Document
  // Verification step exists) that is too — even if the overall application.status still
  // lags at draft. A step counts as submitted once it has left DRAFT(0)/INCOMPLETE(2):
  // SUBMITTED(1), APPROVED(3), or the managed-submit doc state(4). Broker Individual Plus
  // submits form+docs together, which leaves the Document Verification step at status 4 —
  // the same "done" set ApplyIndividualLite uses for the doc step ([1,3,4]).
  const isStepSubmitted = (status) => [1, 3, 4].includes(Number(status));
  const formAndDocsSubmitted = hasSteps
    && isStepSubmitted(stepStatusByLabel["Application Form"])
    && (!("Document Verification" in stepStatusByLabel) || isStepSubmitted(stepStatusByLabel["Document Verification"]));
  // "Continue editing" only makes sense while the form is still the broker's to change:
  // a draft (status 0) or when FV Bank has asked for more info (needsInfo). Once it's
  // submitted/approved/onboarded/failed/declined/archived, editing is locked.
  const canEdit = !accountCreationFailed && (Number(application.status) === 0 || needsInfo);

  const title = business.Business_Name || application.customerName || application.email || "Draft client";
  const applicationNumber = application.applicationNo || "Draft application";
  const clientReference = application.brokerReferenceId || application.customerName || "Not provided";
  // Handles legacy formTypes (BUSINESS_PLUS / BUSINESS_PRIME) and v2 BUSINESS
  // with a numeric sub-type, plus Individual Plus.
  const typeLabel = getFormTypeLabel(application.formType || FORM_TYPE.Business, application.type)
    || application.accountType || "Business";

  // Derive Business-details labels/values from the Business form JSON schema
  // (SysConfig) so labels match the form and option codes render as their labels.
  const fieldSchema = useMemo(() => {
    const labelByField = {};
    const optionByField = {};
    (Array.isArray(payload?.fields) ? payload.fields : []).forEach((f) => {
      if (!f?.field) return;
      if (f.label) labelByField[f.field] = f.label;
      if (Array.isArray(f.options) && f.options.length) {
        const map = {};
        f.options.forEach((o) => {
          if (o && o.value !== undefined && o.value !== null) map[String(o.value)] = o.label;
        });
        optionByField[f.field] = map;
      }
    });
    return { labelByField, optionByField };
  }, [payload?.fields]);

  const businessRows = useMemo(() => {
    const countryMap = application?.extension?.countryMasterMap || {};
    const mapFact = ({ key, label, icon }) => {
      const raw = business[key];
      if (raw === undefined || raw === null || raw === "") return null;
      let value = fieldSchema.optionByField[key]?.[String(raw)]
        || countryMap[String(raw)]?.Country
        || raw;
      if (Array.isArray(value)) value = value.join(", ");
      return { key, label, value, icon };
    };
    // Individual Plus: combined legal name + individual facts (the record is the IndividualLite).
    if (isIndividualPlus) {
      const nameValue = fullName(business);
      const nameRow = nameValue ? [{ key: "fullName", label: "Legal name", value: nameValue, icon: FaUser }] : [];
      return [...nameRow, ...INDIVIDUAL_HEADER_FACTS.map(mapFact).filter(Boolean)];
    }
    return BUSINESS_HEADER_FACTS.map(mapFact).filter(Boolean);
  }, [isIndividualPlus, business, fieldSchema, application]);


  // Per-owner KYC (Ondato) flow status, keyed by owner id. Rows are sorted
  // seq-desc, so the first record per owner is the latest attempt.
  const kycByOwner = useMemo(() => {
    const map = new Map();
    (Array.isArray(payload?.kyc) ? payload.kyc : []).forEach((rec) => {
      const ownerId = rec?.ubo_id || rec?.individual_id || rec?.uboId;
      if (ownerId && !map.has(String(ownerId))) map.set(String(ownerId), rec?.flowStatus);
    });
    return map;
  }, [payload?.kyc]);

  const owners = useMemo(() => {
    // Individual Plus: a single KYC subject (the applicant). Token is the linked Individual id
    // (ubo_id) used by the completion link; the client completes Terms + signature + KYC there.
    if (isIndividualPlus) {
      const subjectId = business?.ubo_id || business?._id;
      if (!subjectId) return [];
      const ondatoStatus = ondatoToKyc(kycByOwner.get(String(subjectId)));
      const email = business.email || "";
      return [{
        id: subjectId,
        name: fullName(business) || email || "Client",
        email,
        type: "Individual",
        isPrimaryUser: true,
        ownership: undefined,
        requiresKyc: true,
        kycStatus: ondatoStatus || toKycStatus(business.kycStatus || business.ondatoStatus || business.verificationStatus),
        url: email ? resolveKycSubjectUrl({ token: subjectId, subjectEmail: email }) : "",
      }];
    }
    return (Array.isArray(business.individuals) ? business.individuals : [])
      .slice()
      .sort((left, right) => Number(Boolean(right?.isPrimaryUser)) - Number(Boolean(left?.isPrimaryUser)))
      .map((person, index) => {
        const ondatoStatus = ondatoToKyc(kycByOwner.get(String(person._id)));
        // Per business rules, only certain owners need KYC (e.g. a 10%
        // shareholder does not) — gate the verification link on this.
        const requiresKyc = isOwnerKycRequired(person, application.formType);
        return {
          id: person._id || `owner-${index}`,
          name: fullName(person) || person.email || `Owner ${index + 1}`,
          email: person.email || "",
          type: ownerTypeLabel(person),
          isPrimaryUser: person.isPrimaryUser === true,
          ownership: person.percentOwner,
          requiresKyc,
          kycStatus: ondatoStatus || toKycStatus(person.kycStatus || person.ondatoStatus || person.verificationStatus),
          url: requiresKyc && person._id && person.email
            ? resolveKycSubjectUrl({ token: person._id, subjectEmail: person.email })
            : "",
        };
      });
  }, [isIndividualPlus, business, kycByOwner, application.formType]);

  const copyOwnerLink = useCallback(async (url, id) => {
    if (!url) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "absolute";
        field.style.left = "-9999px";
        document.body.appendChild(field);
        field.select();
        document.execCommand("copy");
        document.body.removeChild(field);
      }
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((current) => (current === id ? "" : current)), 1800);
      showMessage?.("Verification link copied.");
    } catch (copyError) {
      showError?.("Unable to copy verification link.");
    }
  }, [showError, showMessage]);


  // What the broker should do next — derived from status + owner KYC states.
  // Surfaced as a banner so the page leads with action, not raw data.
  // Only KYC-required owners drive verification progress / next-action — owners
  // below the ownership threshold are excluded so they never read as "pending".
  const ownersWithEmail = owners.filter((o) => o.requiresKyc && o.email);
  // FAILED (rejected) = the owner cannot simply retry; the case waits for an FV
  // Bank manual review, which may then request a redo. REDO_REQUESTED = FV Bank
  // asked the owner to resubmit, so re-sharing the link is the broker's action.
  const failedOwners = ownersWithEmail.filter((o) => o.kycStatus === "FAILED");
  const redoOwners = ownersWithEmail.filter((o) => o.kycStatus === "REDO_REQUESTED");
  // In-flight or not-yet-started owners the broker can still share a link with.
  const pendingOwners = ownersWithEmail.filter((o) => !["COMPLETED", "FAILED", "REDO_REQUESTED"].includes(o.kycStatus));
  const unverifiedOwners = ownersWithEmail.filter((o) => o.kycStatus !== "COMPLETED");
  const verifiedOwners = ownersWithEmail.filter((o) => o.kycStatus === "COMPLETED");
  const clientEmail = application.email || business.email || "";
  const countryFact = businessRows.find((row) => row.key === "Business_Registration_Country");
  const ownerTotal = ownersWithEmail.length || owners.length;
  let ownerVerificationText = "Owners not added";
  if (ownersWithEmail.length) {
    ownerVerificationText = `${verifiedOwners.length} of ${ownersWithEmail.length} verified`;
  } else if (ownerTotal) {
    ownerVerificationText = `${owners.length} owner${owners.length === 1 ? "" : "s"} added`;
  }
  let ownerVerificationState = "Waiting for owners";
  if (owners.length && !ownersWithEmail.length) ownerVerificationState = "Missing owner emails";
  else if (failedOwners.length) ownerVerificationState = `${failedOwners.length} failed`;
  else if (unverifiedOwners.length) ownerVerificationState = `${unverifiedOwners.length} pending`;
  else if (ownersWithEmail.length) ownerVerificationState = "Complete";
  let ownerVerificationTone = "is-pending";
  if (failedOwners.length) ownerVerificationTone = "is-error";
  else if (ownerVerificationState === "Complete") ownerVerificationTone = "is-complete";
  // Resolve the country code to its full name via the country master (e.g. "IN" -> "India").
  // Business: businessRows already resolved it (countryFact). Individual: no Business_Registration_Country
  // row, so look up application.country in the country master directly.
  const countryMaster = application?.extension?.countryMasterMap || {};
  const registeredCountry = countryFact?.value
    || countryMaster[String(application.country)]?.Country
    || application.country
    || "Not available";
  const headerFacts = [
    { key: "product", label: "Product", value: typeLabel },
    { key: "email", label: "Email", value: clientEmail || "Not available" },
    { key: "country", label: "Registered country", value: registeredCountry },
  ];

  // Broker-facing 4-stage tracker (not the raw FV Bank form steps). States:
  // done | current | pending | warning | error. warning (amber "!") = the broker
  // needs to supply missing info — recoverable, not a rejection. error (red "x") =
  // a genuine failure such as KYC Failed.
  const progressSteps = useMemo(() => {
    const n = Number(application.status);
    // The Application Form stage covers form + DOCUMENTS. A step actually sitting at
    // INCOMPLETE/ask-for-info (needsInfo) is a GENUINE "needs broker input" and must always
    // flag — including when the Application Form step itself is approved (3) but FV has
    // re-requested DOCUMENTS (Document Verification at 2). A "Missing Info" TAG, by contrast,
    // can be stale once the form step is approved, so trust the tag only when it isn't.
    const hasMissingInfo = !accountCreationFailed
      && (needsInfo || (!formStepApproved && systemTags.some((tag) => tag.details === "Missing Info")));
    const hasKycFailed = failedOwners.length > 0 || systemTags.some((tag) => tag.details === "KYC Failed");
    const kycComplete = ownersWithEmail.length > 0 && unverifiedOwners.length === 0;
    const onboarded = n === 6 || n === 9;

    let appForm;
    if (hasMissingInfo) appForm = "warning";
    else if (formStepApproved || formAndDocsSubmitted || n >= 1) appForm = "done";
    else if (n === 0) appForm = "current";
    else appForm = "done";

    let kyc;
    if (hasKycFailed) kyc = "error";
    else if (kycComplete) kyc = "done";
    else if (formAndDocsSubmitted || n >= 1) kyc = "current";
    else kyc = "pending";

    let review;
    if (onboarded || n === 3) review = "done";
    else if (accountCreationFailed || n === 5 || (n >= 1 && kycComplete)) review = "current";
    else review = "pending";

    return [
      { label: "Application Form", state: appForm },
      { label: "KYC Verification", state: kyc },
      { label: "Verification In Progress", state: review },
      { label: "Onboarded", state: onboarded ? "done" : "pending" },
    ];
  }, [application.status, accountCreationFailed, formAndDocsSubmitted, needsInfo, formStepApproved, systemTags, failedOwners.length, ownersWithEmail.length, unverifiedOwners.length]);
  const doneCount = progressSteps.filter((step) => step.state === "done").length;
  let nextAction = null;
  if (loading) {
    nextAction = null;
  } else if (accountCreationFailed) {
    // Post-approval FV-side provisioning issue — the broker can't act on it; don't
    // prompt a form edit. Surface it as "with FV Bank" so it reads as in-progress.
    nextAction = { tone: "info", title: "With FV Bank", text: "FV Bank is finalizing this application — no action is needed from you right now. We'll surface any follow-up requests here." };
  } else if (needsInfo) {
    nextAction = { tone: "warning", title: "FV Bank requested more information", text: `FV Bank has returned the application for ${title} to obtain additional information. Please review and update the application with your client, then continue from the application form.`, cta: "edit" };
  } else if (redoOwners.length) {
    // FV Bank reviewed a rejection and asked the individual to redo — actionable now.
    nextAction = { tone: "warning", title: `${redoOwners.length} individual${redoOwners.length > 1 ? "s" : ""} asked to redo verification`, text: "FV Bank reviewed and requested a redo. Re-share the verification link below so the individual can resubmit." };
  } else if (failedOwners.length) {
    // Rejected — not retryable by the broker; it waits for FV Bank manual review.
    nextAction = { tone: "error", title: `${failedOwners.length} identity verification${failedOwners.length > 1 ? "s" : ""} rejected`, text: "These are with FV Bank for manual review. No action needed yet — if the individual must resubmit, FV Bank will request a redo and it will show here." };
  } else if (pendingOwners.length) {
    nextAction = { tone: "info", title: `${pendingOwners.length} individual${pendingOwners.length > 1 ? "s" : ""} still to verify`, text: "Copy each individual's link below and share it from your own channel — each individual can verify independently." };
  } else if (Number(application.status) === 0) {
    // DRAFT — the application form has not been submitted yet. Even when the
    // individuals are verified, the broker must finish and submit the form before
    // FV Bank can review, so prompt them to continue editing (never "nothing needed").
    nextAction = { tone: "info", title: "Finish the application form", text: "This application is still a draft. Continue editing to complete the application form and submit it for FV Bank review.", cta: "edit" };
  } else if (!submitted && ownersWithEmail.length) {
    nextAction = { tone: "success", title: "All individuals verified", text: "Every individual has completed identity verification. Nothing more is needed from you right now — FV Bank will continue the review." };
  } else if (submitted) {
    nextAction = { tone: "info", title: "With FV Bank for review", text: "Any follow-up requests will be displayed here so you can review and take action on them." };
  }

  if (loading && !payload) {
    return <DetailSkeleton />;
  }

  const tagsRow = (
    <div className="broker-command-tags">
      {systemTags.map((tag) => (
        <span
          key={tag.tag_id || tag.details}
          className="broker-system-tag"
          title={`Flagged by FV Bank: ${tag.details}`}
          style={{ color: tag.color, backgroundColor: `${tag.color}1a` }}
        >
          {tag.details}
        </span>
      ))}
      <TagPicker
        value={brokerTags}
        onChange={saveBrokerTags}
        suggestions={brokerTagStore.options}
        saving={savingTags}
        onRenameTag={(tag, label) => brokerTagStore.renameElement(tag, label).catch((e) => showError?.(e))}
        onRecolorTag={(tag, color) => brokerTagStore.recolorElement(tag, color).catch((e) => showError?.(e))}
        onDeleteTag={(tag) => brokerTagStore.deleteElement(tag).catch((e) => showError?.(e))}
      />
    </div>
  );

  return (
    <div className={`broker-page broker-application-detail-page ${notesExpanded ? "is-notes-expanded" : ""}`}>
      {loading && payload ? (
        <div className="broker-detail-refreshing" role="status" aria-live="polite" aria-busy="true" data-testid="broker-detail-refreshing">
          <span className="broker-detail-refreshing__card">
            <FaSync aria-hidden="true" className="broker-detail-refreshing__icon" />
            Refreshing…
          </span>
        </div>
      ) : null}
      <div className="broker-detail-primary">
      <div className="broker-application-command">
        {embedded ? (
          <>
            <span className="broker-command-context">
              <strong>{applicationNumber}</strong>
              <StatusBadge status={application.status} bucket />
            </span>
            <div className="broker-command-actions">
              <button
                type="button"
                className="broker-command-close broker-command-refresh"
                onClick={() => load()}
                disabled={loading}
                aria-label="Refresh"
                title="Refresh"
              >
                <FaSync aria-hidden="true" className={loading ? "broker-command-refresh__icon--spinning" : undefined} />
              </button>
              <button
                type="button"
                className="broker-command-close"
                onClick={() => (onClose ? onClose() : navigate(buildBrokerPath(brokerBasePath, "/applications")))}
                aria-label="Close"
                title="Close"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className="broker-link-button broker-link-button--back"
              onClick={() => navigate(buildBrokerPath(brokerBasePath, "/applications"))}
            >
              <FaArrowLeft aria-hidden="true" /> Applications
            </button>
            <div className="broker-actions">
              <button type="button" className="broker-ghost-button" onClick={openEditInNewTab} disabled={!canEdit}>
                <FaFileAlt aria-hidden="true" /> Continue editing
              </button>
            </div>
          </>
        )}
      </div>

      <header className="broker-head">
        <div className="broker-head__top">
          <div className="broker-head__identity">
            <span className="broker-head__kicker">Client ref · {clientReference}</span>
            <h1 className="broker-head__title">{title}</h1>
            <section className="broker-head__tags broker-head__tags--inline" aria-label="Application tags">
              {tagsRow}
            </section>
          </div>
        </div>

        <div className="broker-head__fact-grid" aria-label="Application summary">
          {headerFacts.map((fact) => (
            <div className="broker-head-fact" key={fact.key}>
              <span className="broker-head-fact__body">
                <span className="broker-head-fact__label">{fact.label}</span>
                <span className="broker-head-fact__value">{formatValue(fact.value)}</span>
              </span>
            </div>
          ))}
        </div>
      </header>

      {error ? (
        <BrokerAlert type="error" title="Application action failed" actionLabel="Reload" onAction={load}>
          {error}
        </BrokerAlert>
      ) : null}

      {nextAction ? (
        <section className={`broker-next-action broker-next-action--${nextAction.tone}`} aria-label="What to do next">
          <div className="broker-next-action__body">
            <strong>{nextAction.title}</strong>
            <span>{nextAction.text}</span>
          </div>
          {nextAction.cta === "edit" ? (
            <button type="button" className="broker-ghost-button" onClick={openEditInNewTab}>
              <FaFileAlt aria-hidden="true" /> Continue editing
            </button>
          ) : null}
        </section>
      ) : null}

      {/* Progress strip from real application steps */}
      {progressSteps.length ? (
        <section className="broker-progress" aria-label="Application progress">
          <div className="broker-progress__head">
            <span className="broker-progress__eyebrow">Progress</span>
            <strong>{doneCount} of {progressSteps.length} stages complete</strong>
          </div>
          <ol className="broker-progress__track">
            {progressSteps.map((step, index) => (
              <li className={`broker-progress__step is-${step.state}`} key={`${step.label}-${index}`}>
                <span className="broker-progress__marker" aria-hidden="true">
                  {step.state === "done" ? <FaCheck /> : null}
                  {step.state === "error" ? <FaTimes /> : null}
                  {step.state === "warning" ? <FaExclamation /> : null}
                  {!["done", "error", "warning"].includes(step.state) ? <span className="broker-progress__dot" /> : null}
                </span>
                <span className="broker-progress__label">{step.label}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

        <div className="broker-detail-workspace__content">
          <div className="broker-detail-grid">
            <main className="broker-detail-grid__main">
          {/* Owners & verification */}
          <section className="broker-detail-card">
            <div className="broker-detail-card__header">
              <div>
                <h3>Individuals &amp; verification</h3>
                <p>Share each individual&rsquo;s link from your own channel. Each individual can verify independently.</p>
              </div>
              <span className="broker-detail-card__count">{owners.length}</span>
            </div>
            {owners.length ? (
              <ul className="broker-owner-list">
                {owners.map((owner) => (
                  <li className="broker-owner-item" key={owner.id}>
                    <span className="broker-owner-item__avatar" aria-hidden="true">{initials(owner.name)}</span>
                    <div className="broker-owner-item__main">
                      <div className="broker-owner-item__top">
                        <strong>{owner.name}</strong>
                        {owner.isPrimaryUser ? <span className="broker-owner-item__tag">Primary</span> : null}
                      </div>
                      <span className="broker-owner-item__meta">
                        {[owner.type, owner.email, owner.ownership ? `${owner.ownership}% owner` : ""].filter(Boolean).join(" · ")}
                      </span>
                      <div className="broker-owner-item__foot">
                        {owner.requiresKyc ? (
                          <>
                            <KycStatusBadge status={owner.kycStatus} />
                            {owner.kycStatus === "FAILED" ? (
                              // Rejected — broker can't retry; awaits FV Bank review.
                              <span className="broker-owner-item__nolink">Awaiting FV Bank review</span>
                            ) : owner.kycStatus === "COMPLETED" ? null
                              : owner.url ? (
                                <button
                                  type="button"
                                  className="broker-copy-link"
                                  onClick={() => copyOwnerLink(owner.url, owner.id)}
                                >
                                  <FaCopy aria-hidden="true" />
                                  {copiedId === owner.id ? "Copied" : "Copy verification link"}
                                </button>
                              ) : (
                                <span className="broker-owner-item__nolink">No email on file</span>
                              )}
                          </>
                        ) : (
                          <span className="broker-owner-item__nolink">KYC not required</span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="broker-owner-empty">Individual details appear once the application form is saved.</p>
            )}
          </section>

            </main>

            <aside className="broker-detail-grid__side">
          {/* Next step */}
          <section className="broker-detail-card broker-detail-next">
            <div className="broker-detail-card__header">
              <div>
                <h3>Next step</h3>
              </div>
            </div>
            <ol className="broker-detail-next__list">
              <li>Copy each individual&rsquo;s verification link above and share it from your own channel.</li>
              <li>Each individual verifies their identity using their own link — they can do this independently.</li>
              <li>
                {submitted
                  ? "Submitted — FV Bank is reviewing this application."
                  : "Complete any remaining form steps before FV Bank review."}
              </li>
            </ol>
          </section>

            </aside>
          </div>

          <footer className="broker-detail-foot">
            <dl className="broker-detail-foot__meta">
              {application.createdBy ? (
                <div>
                  <dt>Created by</dt>
                  <dd><BrokerAdminLabel id={application.createdBy} /></dd>
                </div>
              ) : null}
              <div>
                <dt>Created</dt>
                <dd>{formatDateTimeShort(application.createdAt)}</dd>
              </div>
              <div>
                <dt>Last updated</dt>
                <dd>{formatDateTimeShort(application.updatedAt)}</dd>
              </div>
            </dl>
            <button type="button" className="broker-ghost-button" onClick={openEditInNewTab} disabled={!canEdit}>
              <FaFileAlt aria-hidden="true" /> Continue editing
            </button>
          </footer>
        </div>
      </div>

      <aside className={`broker-notes-sidebar ${notesExpanded ? "is-expanded" : ""}`} aria-label="Application notes">
          {!notesExpanded ? (
            <button
              type="button"
              className="broker-notes-sidebar__toggle"
              onClick={() => setNotesExpanded(true)}
              aria-expanded={false}
              aria-label="Expand application notes"
              title="Expand notes"
            >
              <FaChevronLeft aria-hidden="true" />
              <span>Notes</span>
            </button>
          ) : (
            <div className="broker-notes-sidebar__content">
              <BrokerNotesPanel
                recordOf={applicationId}
                recordType={BROKER_APPLICATION_NOTE_RECORD_TYPE}
                title="Notes"
                subtitle="Broker workspace notes"
                active={notesExpanded}
                onCollapse={() => setNotesExpanded(false)}
              />
            </div>
          )}
        </aside>

    </div>
  );
};

export default observer(BrokerApplicationDetail);
